import { useState } from "react";

const DEFAULT_MARKDOWN = `# Título

Escribe aquí tu contenido en **Markdown**.

## Lista

- Item uno
- Item dos
- Item tres

## Código

\`\`\`js
console.log("Hola markdown");
\`\`\`

## Tareas

- [x] Crear editor
- [ ] Exportar archivo
`;

function escapeHtml(value) {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function renderInline(text) {
  return escapeHtml(text)
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/`(.*?)`/g, "<code>$1</code>")
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g,
      '<a href="$2" target="_blank" rel="noreferrer">$1</a>'
    );
}

function markdownToHtml(markdown) {
  const lines = String(markdown || "").split(/\r?\n/);
  const html = [];
  let inCode = false;
  let codeLines = [];
  let inList = false;

  function closeList() {
    if (inList) {
      html.push("</ul>");
      inList = false;
    }
  }

  lines.forEach((line) => {
    if (line.trim().startsWith("```")) {
      if (!inCode) {
        closeList();
        inCode = true;
        codeLines = [];
      } else {
        html.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
        inCode = false;
      }

      return;
    }

    if (inCode) {
      codeLines.push(line);
      return;
    }

    if (line.startsWith("# ")) {
      closeList();
      html.push(`<h1>${renderInline(line.slice(2))}</h1>`);
      return;
    }

    if (line.startsWith("## ")) {
      closeList();
      html.push(`<h2>${renderInline(line.slice(3))}</h2>`);
      return;
    }

    if (line.startsWith("### ")) {
      closeList();
      html.push(`<h3>${renderInline(line.slice(4))}</h3>`);
      return;
    }

    if (line.startsWith("> ")) {
      closeList();
      html.push(`<blockquote>${renderInline(line.slice(2))}</blockquote>`);
      return;
    }

    if (line.trim() === "---") {
      closeList();
      html.push("<hr />");
      return;
    }

    if (line.startsWith("- [x] ") || line.startsWith("- [ ] ")) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }

      const checked = line.startsWith("- [x] ");
      const content = line.slice(6);

      html.push(
        `<li><input type="checkbox" disabled ${
          checked ? "checked" : ""
        } /> ${renderInline(content)}</li>`
      );
      return;
    }

    if (line.startsWith("- ")) {
      if (!inList) {
        html.push("<ul>");
        inList = true;
      }

      html.push(`<li>${renderInline(line.slice(2))}</li>`);
      return;
    }

    if (!line.trim()) {
      closeList();
      html.push("<br />");
      return;
    }

    closeList();
    html.push(`<p>${renderInline(line)}</p>`);
  });

  closeList();

  if (inCode) {
    html.push(`<pre><code>${escapeHtml(codeLines.join("\n"))}</code></pre>`);
  }

  return html.join("\n");
}

function downloadTextFile(fileName, content, mimeType = "text/markdown") {
  const blob = new Blob([content], {
    type: `${mimeType};charset=utf-8`,
  });

  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = fileName || "documento.md";
  document.body.appendChild(link);
  link.click();

  link.remove();
  URL.revokeObjectURL(url);
}

function MarkdownEditorTool() {
  const [markdown, setMarkdown] = useState(DEFAULT_MARKDOWN);
  const [fileName, setFileName] = useState("documento.md");

  async function importMarkdownFile(event) {
    const file = event.target.files?.[0];

    if (!file) return;

    try {
      const text = await file.text();

      setMarkdown(text);
      setFileName(file.name || "documento.md");
    } catch (error) {
      console.error(error);
      alert("No se pudo leer el archivo.");
    } finally {
      event.target.value = "";
    }
  }

  function downloadMarkdown() {
    downloadTextFile(fileName || "documento.md", markdown, "text/markdown");
  }

  function copyMarkdown() {
    navigator.clipboard
      .writeText(markdown)
      .then(() => alert("Markdown copiado."))
      .catch(() => alert("No se pudo copiar."));
  }

  function clearMarkdown() {
    const confirmed = window.confirm("¿Limpiar todo el contenido?");

    if (!confirmed) return;

    setMarkdown("");
  }

  return (
    <section className="tools-panel markdown-tool">
      <div className="tools-panel__header">
        <div>
          <span className="section-eyebrow">Markdown</span>
          <h3>Editor Markdown</h3>
          <p>Escribe contenido y mira la vista previa en tiempo real.</p>
        </div>

        <div className="tools-output-actions">
          <label className="secondary-button markdown-upload-button">
            Importar .md
            <input
              type="file"
              accept=".md,.markdown,text/markdown,text/plain"
              onChange={importMarkdownFile}
            />
          </label>

          <button
            type="button"
            className="secondary-button"
            onClick={copyMarkdown}
            disabled={!markdown}
          >
            Copiar
          </button>

          <button
            type="button"
            className="primary-action-button"
            onClick={downloadMarkdown}
            disabled={!markdown}
          >
            Descargar
          </button>

          <button
            type="button"
            className="small-button small-button--danger"
            onClick={clearMarkdown}
            disabled={!markdown}
          >
            Limpiar
          </button>
        </div>
      </div>

      <div className="markdown-file-row">
        <label>
          Nombre del archivo
          <input
            type="text"
            value={fileName}
            onChange={(e) => setFileName(e.target.value)}
            placeholder="documento.md"
          />
        </label>
      </div>

      <div className="markdown-editor-grid">
        <div className="markdown-editor-panel">
          <div className="markdown-panel-title">
            <strong>Editor</strong>
            <span>{markdown.length} caracteres</span>
          </div>

          <textarea
            className="markdown-textarea"
            value={markdown}
            onChange={(e) => setMarkdown(e.target.value)}
            placeholder="Escribe Markdown aquí..."
            spellCheck="false"
          />
        </div>

        <div className="markdown-preview-panel">
          <div className="markdown-panel-title">
            <strong>Vista previa</strong>
            <span>Preview simple</span>
          </div>

          <article
            className="markdown-preview"
            dangerouslySetInnerHTML={{
              __html: markdown.trim()
                ? markdownToHtml(markdown)
                : '<p class="empty-state">No hay contenido para previsualizar.</p>',
            }}
          />
        </div>
      </div>
    </section>
  );
}

export default MarkdownEditorTool;